import {
  AllergenIcon, Badge, Button, Card, ErrorState, Field, LoadingState, Notice, PageHeader, Roles, Row, Select, Stack,
  Textarea, ageInMonths, formatAge, useApi, useContactRole, useI18n, withoutIsolates,
  type AllergySeverity, type MedicationResponse,
} from '@daghfal/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

const SEVERITY_TONE: Record<AllergySeverity, 'neutral' | 'warning' | 'danger'> = {
  Mild: 'neutral', Severe: 'danger', Anaphylactic: 'danger',
};

/** What a family may ask the nursery to change (FR-CHD-02): contact and health details, never identity or class. */
const CHANGE_PARTS = ['contacts', 'health', 'pickups', 'medication', 'other'] as const;
type ChangePart = typeof CHANGE_PARTS[number];

/** The administration a request goes to: the roles `GET /conversations/contacts` adds for every family. */
const ADMINISTRATION = new Set<string>([Roles.tenant.owner, Roles.tenant.manager]);

/**
 * The child's own record, as the guardian sees it (FR-HOME-03, FR-CHD-01, SRS §4.12).
 *
 * Read-only. A parent checks what the nursery holds (the allergens, who is on the emergency list, who else may collect
 * the child, the medication in force) and asks for a correction through the nursery, because these fields carry
 * medical and safeguarding weight and the setting has to know when one changes (FR-CHD-02, Q43, PLAN-gaps C5).
 */
export function ChildInfo({ childId }: { childId: number }) {
  const api = useApi();
  const i18n = useI18n();
  const { t, formatDate, formatPhone } = i18n;

  const child = useQuery({
    queryKey: ['child', childId],
    queryFn: () => api.children.get(childId),
  });

  if (child.isPending) return <LoadingState />;
  if (child.isError) return <ErrorState error={child.error} onRetry={() => void child.refetch()} />;

  const data = child.data;

  return (
    <Stack>
      <PageHeader title={t('info.title')} />

      <Card>
        <Stack tight>
          <Detail label={t('info.class')} value={data.className ?? '—'} />
          <Detail label={t('info.dob')} value={formatDate(data.dateOfBirth)} />
          <Detail
            label={t('info.age')}
            value={formatAge(ageInMonths(data.dateOfBirth), i18n)}
          />
          <Detail label={t('info.enrolled')} value={formatDate(data.enrollmentDate)} />
        </Stack>
      </Card>

      <Card title={t('info.allergies')}>
        {data.allergies.length === 0
          ? <p className="dg-card__hint">{t('info.allergies.none')}</p>
          : (
            <Stack>
              {data.allergies.map((allergy) => (
                <Stack key={allergy.id} tight>
                  <Row>
                    <AllergenIcon icon={allergy.icon} allergen={allergy.allergen} severity={allergy.severity} />
                    <strong>{allergy.allergen}</strong>
                    <Badge tone={SEVERITY_TONE[allergy.severity]}>
                      {t(`info.severity.${allergy.severity}`)}
                    </Badge>
                  </Row>
                  {allergy.reaction && <span className="dg-card__hint">{allergy.reaction}</span>}
                </Stack>
              ))}
            </Stack>
          )}
      </Card>

      <Card title={t('info.contacts')}>
        {data.emergencyContacts.length === 0
          ? <p className="dg-card__hint">{t('info.contacts.none')}</p>
          : (
            <Stack>
              {[...data.emergencyContacts].sort((a, b) => a.priority - b.priority).map((contact) => (
                <Row key={contact.id} between>
                  <span className="dg-stack dg-stack--tight">
                    <strong>{contact.name}</strong>
                    {contact.relationship && <span className="dg-card__hint">{contact.relationship}</span>}
                  </span>
                  {/* A tel: link, because on a phone this is a number you ring, not one you read. */}
                  <a href={`tel:${contact.phone}`} dir="ltr">{formatPhone(contact.phone)}</a>
                </Row>
              ))}
            </Stack>
          )}
      </Card>

      <PickupsCard childId={childId} />
      <MedicationsCard childId={childId} />
      <ChangeRequest childId={childId} childName={data.preferredName ?? data.firstName} />
    </Stack>
  );
}

/** Who besides the guardians may collect the child, as the door checks it (FR-CHD-01, §4.12.4). */
function PickupsCard({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatDate, formatPhone } = useI18n();
  const pickups = useQuery({ queryKey: ['pickups', childId], queryFn: () => api.pickups.list(childId) });

  return (
    <Card title={t('info.pickups')} hint={t('info.pickups.hint')}>
      {pickups.isPending && <LoadingState />}
      {pickups.isError && <ErrorState error={pickups.error} onRetry={() => void pickups.refetch()} />}
      {pickups.data?.items.length === 0 && <p className="dg-card__hint">{t('info.pickups.none')}</p>}
      <Stack>
        {pickups.data?.items.map((pickup) => (
          <Row key={pickup.id} between>
            <span className="dg-stack dg-stack--tight">
              <strong>{pickup.fullName}</strong>
              {pickup.relationship && <span className="dg-card__hint">{pickup.relationship}</span>}
              {pickup.validFrom && <span className="dg-card__hint">{t('info.pickups.from', { date: formatDate(pickup.validFrom) })}</span>}
              {pickup.validTo && <span className="dg-card__hint">{t('info.pickups.until', { date: formatDate(pickup.validTo) })}</span>}
            </span>
            <span className="dg-stack dg-stack--tight">
              <a href={`tel:${pickup.phone}`} dir="ltr">{formatPhone(pickup.phone)}</a>
              {!pickup.isValidToday && <Badge tone="neutral">{t('info.pickups.expired')}</Badge>}
            </span>
          </Row>
        ))}
      </Stack>
    </Card>
  );
}

/** The medication the nursery holds for the child and what was given today; guardians enter none (Q17). */
function MedicationsCard({ childId }: { childId: number }) {
  const api = useApi();
  const { t } = useI18n();
  const medications = useQuery({
    queryKey: ['medications', childId],
    queryFn: () => api.health.medications(childId),
  });

  return (
    <Card title={t('info.medications')} hint={t('info.medications.hint')}>
      {medications.isPending && <LoadingState />}
      {medications.isError && <ErrorState error={medications.error} onRetry={() => void medications.refetch()} />}
      {medications.data?.length === 0 && <p className="dg-card__hint">{t('info.medications.none')}</p>}
      <Stack>
        {medications.data?.map((medication) => <MedicationRow key={medication.id} medication={medication} />)}
      </Stack>
    </Card>
  );
}

function MedicationRow({ medication }: { medication: MedicationResponse }) {
  const { t, tn, formatDate, locale } = useI18n();
  const form = (locale === 'en' && medication.formNameEn) || medication.formNameAr;
  const unit = (locale === 'en' ? medication.unitEn : medication.unitAr) ?? medication.unitAr;
  const dose = unit ? t('medication.doseWithUnit', { dose: medication.dose, unit }) : medication.dose;

  return (
    <Stack tight>
      <Row between>
        <strong>{medication.medicationName}</strong>
        <Badge tone={medication.isCurrent ? 'success' : 'neutral'}>
          {t(medication.isCurrent ? 'medication.current' : 'medication.ended')}
        </Badge>
      </Row>
      <span>{`${form} · ${dose} · ${medication.schedule}`}</span>
      <span className="dg-card__hint">
        {medication.endDate
          ? t('medication.period', { from: formatDate(medication.startDate), to: formatDate(medication.endDate) })
          : t('medication.since', { from: formatDate(medication.startDate) })}
      </span>
      {medication.isCurrent && (
        <span className="dg-card__hint">{tn('info.medication.givenToday', medication.dosesToday)}</span>
      )}
    </Stack>
  );
}

/**
 * Asking the administration to correct a detail (FR-CHD-02, PLAN-gaps C5): a message about this child, its subject
 * naming the part, which opens in Messages once sent so the reply is where the family looks for it. The staff make the
 * change (Q43). Without anyone in the administration to write to, the screen says to contact the nursery instead.
 */
function ChangeRequest({ childId, childName }: { childId: number; childName: string }) {
  const api = useApi();
  const { t } = useI18n();
  const contactRole = useContactRole();
  const navigate = useNavigate();
  const [part, setPart] = useState<ChangePart>('contacts');
  const [recipient, setRecipient] = useState<number | ''>('');
  const [body, setBody] = useState('');

  const contacts = useQuery({ queryKey: ['contacts'], queryFn: () => api.conversations.contacts() });
  const administration = (contacts.data ?? []).filter((contact) => contact.roleKey && ADMINISTRATION.has(contact.roleKey));
  const to = recipient === '' ? administration[0]?.membershipId : recipient;

  const send = useMutation({
    mutationFn: (membershipId: number) => api.conversations.start({
      participantMembershipIds: [membershipId],
      childContextId: childId,
      subject: withoutIsolates(t('change.subject', { part: t(`change.part.${part}`), name: childName })),
      body: body.trim(),
    }),
    onSuccess: (message) => navigate(`/child/${childId}/messages?conversation=${message.conversationId}`),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (to !== undefined && body.trim()) send.mutate(to);
  };

  if (contacts.isPending) return null;
  if (administration.length === 0) return <Notice tone="info">{t('info.contacts.hint')}</Notice>;

  return (
    <Card title={t('change.title')} hint={t('change.hint')}>
      <form onSubmit={submit}>
        <Stack>
          {send.isError && <ErrorState error={send.error} />}
          <Field label={t('change.part')}>
            {(props) => (
              <Select {...props} value={part} onChange={(event) => setPart(event.target.value as ChangePart)}>
                {CHANGE_PARTS.map((option) => <option key={option} value={option}>{t(`change.part.${option}`)}</option>)}
              </Select>
            )}
          </Field>
          {administration.length > 1 && (
            <Field label={t('change.to')}>
              {(props) => (
                <Select {...props} value={to} onChange={(event) => setRecipient(Number(event.target.value))}>
                  {administration.map((contact) => (
                    <option key={contact.membershipId} value={contact.membershipId}>
                      {contact.name ?? '—'}{contactRole(contact) ? ` · ${contactRole(contact)}` : ''}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <Field label={t('change.body')} hint={t('change.body.hint')} required>
            {(props) => <Textarea {...props} rows={3} value={body} onChange={(event) => setBody(event.target.value)} />}
          </Field>
          <Row>
            <Button type="submit" busy={send.isPending} busyLabel={t('action.saving')} disabled={!body.trim()}>
              {t('change.send')}
            </Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Row between>
      <span className="dg-card__hint">{label}</span>
      <strong>{value}</strong>
    </Row>
  );
}
